import {
  afterNextRender,
  Component,
  computed,
  contentChildren,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin
} from '@angular/cdk/overlay';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { merge, Subject, EMPTY, Observable } from 'rxjs';
import { distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';
import { toObservable, outputToObservable } from '@angular/core/rxjs-interop';
import { ControlValueAccessor } from '@angular/forms';

import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { DropdownItemComponent } from '../dropdown/dropdown-item.component';
import { ModalService } from '../modal/modal.service';
import { KeyCode } from '../../domain/key-code.enum';
import { PlatformService } from '../../services/platform.service';
import type {
  ConnectedOverlayPositionChange,
  VerticalConnectionPos
} from '@angular/cdk/overlay';
import type {
  AfterContentInit,
  OnDestroy,
  OnInit,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  WritableSignal
} from '@angular/core';
import type { SelectableItem, SelectionChangeEvent } from '../../domain/selectable.domain';

let uniqueId = 0;

@Component({
  selector: 'td-wb-new-typeahead',
  templateUrl: 'new-typeahead.component.html',
  styleUrls: ['typeahead.component.scss'],
  exportAs: 'tdWbNewTypeahead',
  providers: [ValueAccessorProviderFactory(NewTypeAheadComponent)],
  imports: [CdkOverlayOrigin, CdkConnectedOverlay]
})
export class NewTypeAheadComponent<T> implements OnInit, AfterContentInit, OnDestroy, ControlValueAccessor {
  // Inputs
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-label' });
  readonly placeholder: InputSignal<string | undefined> = input<string | undefined>(undefined);
  readonly uppercase: InputSignal<boolean> = input<boolean>(false);
  readonly preSelectFirstItem: InputSignal<boolean> = input<boolean>(true);
  readonly overrideOverlayOrigin: InputSignal<CdkOverlayOrigin | undefined> = input<CdkOverlayOrigin | undefined>(undefined);

  readonly value: ModelSignal<string | null> = model<string | null>(null);
  readonly disabled: InputSignal<boolean> = input<boolean>(false);
  readonly idValue: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'id' });
  readonly required: InputSignal<boolean> = input<boolean>(false);

  // Outputs
  readonly disabledChange: OutputEmitterRef<boolean> = output<boolean>();
  readonly onSelect: OutputEmitterRef<{ selected: T }> = output<{ selected: T }>();
  readonly focus: OutputEmitterRef<FocusEvent> = output<FocusEvent>();
  readonly blur: OutputEmitterRef<FocusEvent> = output<FocusEvent>();
  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();
  readonly selectedChanged: Subject<T> = new Subject<T>();

  // Queries
  readonly connectedOverlay: Signal<CdkConnectedOverlay | undefined> = viewChild(CdkConnectedOverlay);
  readonly inputElement: Signal<ElementRef<HTMLInputElement> | undefined> = viewChild<ElementRef<HTMLInputElement>>('inputElement');
  readonly items: Signal<ReadonlyArray<DropdownItemComponent<T>>> = contentChildren(DropdownItemComponent, { descendants: true });

  // State Signals
  readonly disabledByCva: WritableSignal<boolean> = signal<boolean>(false);
  readonly internalDisabled: Signal<boolean> = computed<boolean>(() => this.disabled() || this.disabledByCva());
  readonly panelDisplayed: WritableSignal<boolean> = signal<boolean>(false);
  readonly panelPosition: WritableSignal<VerticalConnectionPos> = signal<VerticalConnectionPos>('bottom');
  readonly toggleRect: WritableSignal<DOMRect | undefined> = signal<DOMRect | undefined>(undefined);
  readonly uniqueIdValue: WritableSignal<number> = signal<number>(0);
  readonly platformIOS: WritableSignal<boolean> = signal<boolean>(false);
  readonly activeItem: WritableSignal<DropdownItemComponent<T> | null> = signal<DropdownItemComponent<T> | null>(null);

  // Observables
  private readonly items$: Observable<ReadonlyArray<DropdownItemComponent<T>>> = toObservable(this.items);
  private readonly selectionChanges$: Observable<SelectionChangeEvent<SelectableItem<T>>> = this.items$.pipe(
    switchMap((items: ReadonlyArray<DropdownItemComponent<T>>) => {
      if (items && items.length > 0) {
        return merge(...items.map((item: DropdownItemComponent<T>) => outputToObservable(item.onSelectionChange)));
      }
      return EMPTY;
    })
  );

  // Computed
  readonly id: Signal<string> = computed((): string => this.idValue() ? this.idValue() + '-input' : `td-wb-typeahead-${this.uniqueIdValue()}`);
  readonly itemIds: Signal<string> = computed((): string => this.items().map((item: DropdownItemComponent<T>): string => item.id).join(' '));
  readonly isOpen: Signal<boolean> = computed((): boolean => this.panelDisplayed() && this.items().length > 0);

  // Key Manager
  keyManager: ActiveDescendantKeyManager<DropdownItemComponent<T>>;
  private readonly keyManagerDestroy$: Subject<void> = new Subject<void>();

  // Private properties
  private readonly platformService = inject(PlatformService);
  private readonly elementRef = inject(ElementRef);
  private readonly modalService = inject(ModalService);
  private readonly injector = inject(Injector);

  private readonly destroy: Subject<void> = new Subject<void>();
  private readonly closed: Subject<void> = new Subject<void>();
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};
  private skipOpenOnFocus: boolean = false;
  private lastOpenedValue: string | null = null;
  private previousDisabledState: boolean = false;

  constructor() {
    toObservable(this.internalDisabled).pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy)
    ).subscribe((isDisabled: boolean): void => {
      if (isDisabled !== this.previousDisabledState) {
        this.disabledChange.emit(isDisabled);
        this.previousDisabledState = isDisabled;
      }
    });
  }

  ngOnInit(): void {
    this.uniqueIdValue.set(uniqueId++);
    this.platformIOS.set(this.platformService.isIOS());
  }

  ngOnDestroy(): void {
    this.modalService.autoSuggestPositionAdjustment(false);
    this.destroy.next();
    this.destroy.complete();
    this.closed.next();
    this.closed.complete();
  }

  ngAfterContentInit(): void {
    // Handle selection changes from items
    this.selectionChanges$.pipe(
      takeUntil(this.destroy)
    ).subscribe((event: SelectionChangeEvent<SelectableItem<T>>): void => {
      this.handleSelectionChange(event);
    });

    // Handle key manager and pre-selection on item changes
    this.items$.pipe(
      takeUntil(this.destroy)
    ).subscribe((): void => {
      this.initKeyManager();
      if (this.preSelectFirstItem()) {
        Promise.resolve()
          .then((): void => this.keyManager.setFirstItemActive());
      }
    });
  }

  // Handle input events
  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const text = inputElement.value;
    const newValue = text !== undefined && this.uppercase() ? text.toUpperCase() : text;
    this.value.set(newValue);
    this.onChangeCallback(newValue);
    if (newValue) {
      this.open();
    } else {
      this.close();
    }
  }

  onInputFocus(event: FocusEvent): void {
    const currentValue = this.value();
    if (!this.skipOpenOnFocus && currentValue && currentValue !== this.lastOpenedValue) {
      this.open();
    }
    this.skipOpenOnFocus = false;
    this.focus.emit(event);
  }

  isSkipOpenOnFocus(): boolean {
    return this.skipOpenOnFocus;
  }

  onInputBlur(event: FocusEvent): void {
    this.blur.emit(event);
    this.onTouchedCallback();
    this.touch.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.internalDisabled()) {
      this.isOpen() ? this.onKeyDownOpen(event) : this.onKeyDownClose(event);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null | undefined): void {
    this.value.set(value ?? null);
    const inputEl = this.inputElement();
    if (inputEl) {
      inputEl.nativeElement.value = this.getDisplayValue(value);
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByCva.set(isDisabled);
  }

  // Component methods
  onAttached(): void {
    const destroyedOrClosed = merge(this.closed, this.destroy);
    const overlay = this.connectedOverlay();
    if (overlay) {
      overlay.overlayRef.detachments().pipe(take(1), takeUntil(destroyedOrClosed)).subscribe((): void => {
        this.close();
      });
    }
  }

  close(): void {
    this.modalService.autoSuggestPositionAdjustment(false);
    if (this.panelDisplayed()) {
      this.panelDisplayed.set(false);
      this.lastOpenedValue = this.value();
      this.keyManager.setActiveItem(undefined);
      this.closed.next();
    }
  }

  open(): void {
    this.modalService.autoSuggestPositionAdjustment();
    this.toggleRect.set(this.elementRef.nativeElement.getBoundingClientRect());
    this.lastOpenedValue = this.value();
    this.panelDisplayed.set(true);
  }

  detach(): void {
    this.close();
  }

  showVhOptions(): boolean {
    return this.platformIOS() && this.isOpen() && this.items().length > 0;
  }

  onPositionChange(event: ConnectedOverlayPositionChange): void {
    this.panelPosition.set(event.connectionPair.originY);
  }

  onKeyDownOpen(event: KeyboardEvent): void {
    const keyCode = event.key;
    const isArrowKey = keyCode === KeyCode.ARROW_DOWN || keyCode === KeyCode.ARROW_UP;
    const keyManager = this.keyManager;
    let preventDefault = true;

    if (keyCode === KeyCode.HOME) {
      keyManager.setFirstItemActive();
    } else if (keyCode === KeyCode.END) {
      keyManager.setLastItemActive();
    } else if (isArrowKey && event.altKey) {
      this.close();
    } else if (keyCode === KeyCode.ENTER) {
      if (keyManager.activeItem === undefined || keyManager.activeItem === null) {
        preventDefault = true;
      } else {
        keyManager.activeItem.select(true);
      }
    } else {
      keyManager.onKeydown(event);
      preventDefault = false;
    }
    if (preventDefault) {
      event.preventDefault();
    }
  }

  onKeyDownClose(event: KeyboardEvent): void {
    const keyCode = event.key;
    const isArrowKey = [KeyCode.ARROW_DOWN, KeyCode.ARROW_UP, KeyCode.ARROW_LEFT, KeyCode.ARROW_RIGHT].includes(keyCode as KeyCode);
    if (keyCode === KeyCode.ENTER || isArrowKey) {
      event.preventDefault();
      this.open();
    } else if (keyCode === KeyCode.SPACE) {
      this.open();
    } else {
      this.keyManager.onKeydown(event);
    }
  }

  private handleSelectionChange(event: SelectionChangeEvent<SelectableItem<T>>): void {
    if (event.fromView && this.isOpen()) {
      event.source.deselect();
      this.selectedChanged.next(event.source.getValue());
      const selectedValue = String(event.source.getValue());
      this.value.set(selectedValue);
      this.onChangeCallback(selectedValue);
      this.onTouchedCallback();
      this.touch.emit();
      this.onSelect.emit({ selected: event.source.getValue() });
      this.close();
      this.skipOpenOnFocus = true;
      this.doFocus();
    }
  }

  private doFocus(): void {
    // Using afterNextRender to ensure DOM is ready before focusing
    afterNextRender((): void => {
      const inputEl = this.inputElement()?.nativeElement;
      if (inputEl && document.activeElement === inputEl) {
        this.onInputFocus(new FocusEvent('focus'));
      } else {
        inputEl?.focus();
      }
    }, { injector: this.injector });
  }

  private getDisplayValue(value: string | null): string {
    return value === undefined || value === null ? '' : value;
  }

  private initKeyManager(): void {
    this.keyManagerDestroy$.next();
    this.keyManager = new ActiveDescendantKeyManager<DropdownItemComponent<T>>(this.items())
      .withVerticalOrientation()
      .withHorizontalOrientation('ltr');

    const destroyed = merge(this.destroy, this.keyManagerDestroy$);

    this.keyManager.tabOut.pipe(takeUntil(destroyed)).subscribe((): void => {
      this.close();
    });

    this.keyManager.change.pipe(takeUntil(destroyed)).subscribe((): void => {
      this.activeItem.set(this.keyManager.activeItem);
      if (!this.panelDisplayed() && this.keyManager.activeItem !== null) {
        this.keyManager.activeItem.select(true);
      }
    });

    this.activeItem.set(this.keyManager.activeItem);
  }

}
